<template>
    <v-sheet
        :color="isHighlighted ? 'white' : 'transparent'"
        :border="isHighlighted ? 'sm' : 'none'"
        rounded
        class="transfer-notice"
        :class="cssClasses"
    >
        <v-icon icon="kdi-arrow-left-right" size="22" :color="isHighlighted ? 'yellow-darken-1' : 'grey-darken-1'" />
        <div class="d-flex align-center px-2">
            <span class="text-h6 font-weight-bold">{{ t('trips.resources.tripLegLabels.transfer', 'Transfer') }}:&nbsp;</span>
            <v-icon v-if="isShortTransfer" icon="mdi-clock-fast" size="24" class="mr-1" />
            <span :class="isHighlighted ? '' : 'text-h6 font-weight-bold'" v-html="transferLabel"></span>
        </div>

        <component
            :is="dialogComponent"
            v-if="isShortTransfer"
            v-model="dialog"
            activator="parent"
            width="450"
            class="transfer-notice__dialog"
        >
            <v-card :rounded="isMobile ? 't-lg' : 'lg'" class="pa-4 pa-md-6">
                <div class="transfer-notice__dialog-header mb-6 text-black">
                    <div>
                        {{ t('trips.resources.transferNotice.dialogTitle', 'Info about transfers') }}
                    </div>
                    <v-spacer />
                    <v-icon size="30" icon="mdi-close-circle" @click="dialog = false" />
                </div>

                <v-sheet color="grey-lighten-3" rounded="lg" class="pa-3 pa-md-4 mb-4 mb-md-6">
                    <v-chip label color="yellow-darken-1" variant="tonal" class="notification-badge">
                        <template #prepend>
                            <v-icon icon="kdi-arrow-left-right" size="28" color="yellow-darken-1" />
                        </template>
                        <span class="text-h6 line-height-1 on-background ml-1">
                            {{ t('trips.resources.transferNotice.shortTransferTitle', 'Short transfer time') }}
                        </span>
                    </v-chip>
                    <div class="mt-2">
                        <span v-html="notificationText" />
                    </div>
                </v-sheet>

                <v-card-actions v-if="!isMobile" class="pa-0">
                    <v-spacer />
                    <v-btn variant="flat" color="primary" size="large" @click="dialog = false">
                        <span class="text-h6 font-weight-bold">{{ t('common.resources.back', 'Back') }}</span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </component>
    </v-sheet>
</template>

<script setup lang="ts">
import { useDurationHelpers } from '@/features/common/composables';
import { SHORT_TRANSFER_THRESHOLD_IN_SECONDS } from '@/features/common/constants';
import { LegDto } from '@/types/webapi';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { VDialog, VBottomSheet } from 'vuetify/components';
import { getTransferTime } from '../../helpers';

interface Props {
    /** The next leg in the trip */
    toLeg: LegDto;

    /** The previous leg in the trip */
    fromLeg: LegDto;

    /** The duration of the transfer */
    transferDuration?: number;
}

const props = withDefaults(defineProps<Props>(), { transferDuration: 0 });
const { t, te } = useI18n();
const { mobile: isMobile } = useDisplay();
const { humanizeDuration } = useDurationHelpers();
const dialog = ref(false);
const dialogComponent = computed(() => (isMobile.value ? VBottomSheet : VDialog));
const isGuaranteed = computed<boolean>(() => !!(props.fromLeg.interchangeTo?.guaranteed && props.toLeg.interchangeFrom?.guaranteed));
const hasWaitTime = computed<boolean>(
    () => (props.fromLeg.interchangeTo?.maximumWaitTime || 0) > 0 && (props.toLeg.interchangeFrom?.maximumWaitTime || 0) > 0
);
const notificationText = computed(() => {
    let text = t('trips.resources.transferNotice.shortTransferText', 'The transfer time is short, but you should still be careful.');

    if (te('trips.resources.transferNotice.travelGuaranteeAboutPageLinkUrl')) {
        text += ` <a href="${t('trips.resources.transferNotice.travelGuaranteeAboutPageLinkUrl')}" target="_blank" class="text-link">${t('trips.resources.transferNotice.travelGuaranteeAboutLinkText', 'Read more about travel guarantee')}</a>`;
    }

    return text;
});
const transferTime = computed(() => {
    if (!props.toLeg || !props.fromLeg) return undefined;

    if (isGuaranteed.value) {
        if (hasWaitTime.value) {
            return Math.max(props.fromLeg.interchangeTo!.maximumWaitTime!, props.toLeg.interchangeFrom!.maximumWaitTime!);
        }

        return undefined;
    }

    return getTransferTime(props.fromLeg, props.toLeg, { duration: props.transferDuration });
});
const isShortTransfer = computed<boolean>(() => !!(transferTime.value && transferTime.value <= SHORT_TRANSFER_THRESHOLD_IN_SECONDS));
const transferLabel = computed(() => {
    if (isGuaranteed.value) {
        if (hasWaitTime.value) {
            return t(
                'trips.resources.transferNotice.guaranteedTransferWithWaitTime',
                [humanizeDuration(transferTime.value)],
                'Wait up to {0} &#128077;'
            );
        }

        return t('trips.resources.transferNotice.guaranteedTransfer', '&#9989; Departures correspond');
    }

    if (transferTime.value) {
        if (isShortTransfer.value) {
            return t('trips.resources.transferNotice.shortTransfer', [humanizeDuration(transferTime.value)], 'A bit tight ({0})');
        }

        return t('trips.resources.transferNotice.longTransfer', 'No rush &#9749;');
    }

    return undefined;
});
const isHighlighted = computed(() => transferTime.value && transferTime.value <= SHORT_TRANSFER_THRESHOLD_IN_SECONDS);
const cssClasses = computed(() => ({
    'transfer-notice--highlighted': isHighlighted.value,
    'cursor-pointer': isShortTransfer.value
}));
</script>

<style lang="scss">
@use 'vuetify/settings' as *;
@use 'sass:map';

.transfer-notice {
    display: inline-flex;
    align-items: center;
    position: relative;
    margin-top: 4px;

    &--highlighted {
        border-color: var(--v-theme-secondary);
        opacity: 1;
    }

    &__dialog {
        &-header {
            display: flex;
            align-items: flex-start;
            font-size: calc(20px / $font-size-root) * 1rem;
            line-height: calc(30px / 24px);
            font-weight: 700;

            @media #{map.get($display-breakpoints, 'md-and-up')} {
                font-size: calc(24px / $font-size-root) * 1rem;
            }
        }
    }
}
</style>
